import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@/services/adminConsoleService';

interface ToggleUserStatusModalProps {
  open: boolean;
  onClose: () => void;
  personId: number;
  personName: string;
  isEnabled: boolean;
  onSuccess?: (personId: number, newEnabledState: boolean) => void;
}

const ToggleUserStatusModal: React.FC<ToggleUserStatusModalProps> = ({
  open,
  onClose,
  personId,
  personName,
  isEnabled,
  onSuccess,
}) =>
{
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDisabling = isEnabled;
  const title = isDisabling ? 'Disable User' : 'Restore User';
  const description = isDisabling
    ? `Are you sure you want to disable the user "${personName}" from Corgee?`
    : `Are you sure you want to restore the user "${personName}"?`;
  const confirmLabel = isDisabling ? 'Disable' : 'Restore';

  const handleConfirm = async () =>
  {
    setSaving(true);
    setError(null);
    try
    {
      await adminConsoleService.toggleUserStatus(personId, !isDisabling);
      onSuccess?.(personId, !isEnabled);
      onClose();
    }
    catch
    {
      setError(`Failed to ${isDisabling ? 'disable' : 'restore'} the user.`);
    }
    finally
    {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10003] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 bg-white rounded shadow-xl p-6 w-[400px]">
        <h3 className="static text-xl text-[#4887be] text-[21px] leading-[21px] font-sans font-medium">
          {title}
        </h3>
        <div
          className="icon-incorrect-icon text-brightGray text-[14px] absolute right-[18px] top-5 cursor-pointer p-2"
          onClick={() => onClose?.()}
        />
        <p className="text-[13px] text-[#555] mb-5">{description}</p>
        {error && <p className="text-[11px] text-[#e57373] mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#999] text-white rounded hover:bg-[#777] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50"
          >
            {saving ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ToggleUserStatusModal;
